import React from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import "@/App.css";
import { AuthProvider } from "@/contexts/AuthContext";
import { JobsProvider } from "@/contexts/JobsContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Dashboard from "@/pages/Dashboard";
import Brokerages from "@/pages/Brokerages";
import Realtors from "@/pages/Realtors";
import ScrapeHistory from "@/pages/ScrapeHistory";
import Settings from "@/pages/Settings";
import Users from "@/pages/Users";
import ImportRealtorCa from "@/pages/ImportRealtorCa";
import Cleanup from "@/pages/Cleanup";
import CleanupLog from "@/pages/CleanupLog";
import OpenHouses from "@/pages/OpenHouses";
import BrokerageDetailPage from "@/pages/BrokerageDetailPage";
import RecycleBin from "@/pages/RecycleBin";
import Outreach from "@/pages/Outreach";
import Templates from "@/pages/Templates";
import Locations from "@/pages/Locations";
import TerritoryImport from "@/pages/TerritoryImport";
import AdminDashboard from "@/pages/AdminDashboard";
import ErrorsTab from "@/pages/ErrorsTab";
import BackupTab from "@/pages/BackupTab";
import LogInteraction from "@/pages/LogInteraction";
import Billing from "@/pages/Billing";
import BillingReturn from "@/pages/BillingReturn";
import AdminBilling from "@/pages/AdminBilling";
import BackendHealthGate from "@/components/BackendHealthGate";
import ErrorBoundary from "@/components/ErrorBoundary";
import { Toaster } from "@/components/ui/sonner";
import { installErrorReporter } from "@/lib/errorReporter";

// Install the global browser error reporter exactly once on module load.
// Captures window.onerror, unhandledrejection and axios 5xx responses,
// dedupes, rate-limits and ships them to /api/log/client-error.
installErrorReporter();

export default function App() {
  return (
    <BackendHealthGate>
      <BrowserRouter>
        <AuthProvider>
        <JobsProvider>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          {/* Public one-tap interaction logging — linked from exported contact cards */}
          <Route path="/log/:realtorId/:userId/:sig" element={<LogInteraction />} />
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <Layout>
                  <Dashboard />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/brokerages"
            element={
              <ProtectedRoute>
                <Layout>
                  <Brokerages />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/brokerages/:brokerageId"
            element={
              <ProtectedRoute>
                <Layout>
                  <BrokerageDetailPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/realtors"
            element={
              <ProtectedRoute>
                <Layout>
                  <ErrorBoundary>
                    <Realtors />
                  </ErrorBoundary>
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/realtors/:realtorId"
            element={
              <ProtectedRoute>
                <Layout>
                  <ErrorBoundary>
                    <Realtors />
                  </ErrorBoundary>
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/scrape-history"
            element={
              <ProtectedRoute>
                <Layout>
                  <ScrapeHistory />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/import-realtor-ca"
            element={
              <ProtectedRoute>
                <Layout>
                  <ImportRealtorCa />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <Layout>
                  <Settings />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/users"
            element={
              <ProtectedRoute adminOnly>
                <Layout>
                  <ErrorBoundary>
                    <Users />
                  </ErrorBoundary>
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/cleanup"
            element={
              <ProtectedRoute>
                <Layout>
                  <Cleanup />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/cleanup/log"
            element={
              <ProtectedRoute>
                <Layout>
                  <CleanupLog />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/open-houses"
            element={
              <ProtectedRoute>
                <Layout>
                  <OpenHouses />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/recycle-bin"
            element={
              <ProtectedRoute>
                <Layout>
                  <RecycleBin />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/locations"
            element={
              <ProtectedRoute adminOnly>
                <Layout>
                  <Locations />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin-dashboard"
            element={
              <ProtectedRoute adminOnly>
                <Layout>
                  <AdminDashboard />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin-errors"
            element={
              <ProtectedRoute adminOnly>
                <Layout>
                  <ErrorsTab />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin-backups"
            element={
              <ProtectedRoute adminOnly>
                <Layout>
                  <BackupTab />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/territory"
            element={
              <ProtectedRoute>
                <Layout>
                  <TerritoryImport />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/outreach"
            element={
              <ProtectedRoute>
                <Layout>
                  <Outreach />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/templates"
            element={
              <ProtectedRoute>
                <Layout>
                  <Templates />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/billing"
            element={
              <ProtectedRoute>
                <Layout>
                  <Billing />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/billing/return"
            element={
              <ProtectedRoute>
                <Layout>
                  <BillingReturn />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin-billing"
            element={
              <ProtectedRoute adminOnly>
                <Layout>
                  <AdminBilling />
                </Layout>
              </ProtectedRoute>
            }
          />
        </Routes>
        <Toaster
          position="bottom-right"
          toastOptions={{
            className: "border border-slate-200",
          }}
        />
        </JobsProvider>
      </AuthProvider>
    </BrowserRouter>
    </BackendHealthGate>
  );
}
